// ── API key ──────────────────────────────────────────────────────────────────
export function getOpenAIKey(): string {
  return localStorage.getItem("genius-labs:openai-key") || "";
}
export function setOpenAIKey(key: string) {
  localStorage.setItem("genius-labs:openai-key", key.trim());
}

// ── Mindmap types ─────────────────────────────────────────────────────────────
export interface MindmapLeaf {
  label: string;
  note?: string;
}
export interface MindmapBranch {
  label: string;
  children: MindmapLeaf[];
}
export interface MindmapData {
  root: string;
  branches: MindmapBranch[];
  generatedAt: string;
  sourceTitle?: string;
}

export interface IdeaNode {
  id: string;
  title: string;
  body: string;
  tags: string[];
  color: string;
  createdAt: string;
  x: number;
  y: number;
}

export interface IdeaEdge {
  id: string;
  fromId: string;
  toId: string;
  label: string;
  reason: string;
  aiGenerated: boolean;
}

export interface GeniusLabsData {
  nodes: IdeaNode[];
  edges: IdeaEdge[];
  mindmap?: MindmapData | null;
}

const NODE_COLORS = [
  "#6366f1", // indigo
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#84cc16", // lime
];

function key(projectId: string) {
  return `genius-labs:${projectId}`;
}

function load(projectId: string): GeniusLabsData {
  try {
    const raw = localStorage.getItem(key(projectId));
    if (raw) return JSON.parse(raw);
  } catch {}
  return { nodes: [], edges: [] };
}

function save(projectId: string, data: GeniusLabsData) {
  localStorage.setItem(key(projectId), JSON.stringify(data));
}

export function getGeniusLabs(projectId: string): GeniusLabsData {
  return load(projectId);
}

export function addIdea(
  projectId: string,
  title: string,
  body: string,
  tags: string[],
  canvasWidth = 800,
  canvasHeight = 500
): IdeaNode {
  const data = load(projectId);
  const color = NODE_COLORS[data.nodes.length % NODE_COLORS.length];
  const node: IdeaNode = {
    id: crypto.randomUUID(),
    title: title.trim(),
    body: body.trim(),
    tags,
    color,
    createdAt: new Date().toISOString(),
    x: 80 + Math.random() * (canvasWidth - 160),
    y: 80 + Math.random() * (canvasHeight - 160),
  };
  data.nodes.push(node);
  save(projectId, data);
  return node;
}

export function updateIdea(
  projectId: string,
  id: string,
  updates: Partial<Pick<IdeaNode, "title" | "body" | "tags" | "x" | "y">>
): void {
  const data = load(projectId);
  const idx = data.nodes.findIndex((n) => n.id === id);
  if (idx !== -1) {
    data.nodes[idx] = { ...data.nodes[idx], ...updates };
    save(projectId, data);
  }
}

export function deleteIdea(projectId: string, id: string): void {
  const data = load(projectId);
  data.nodes = data.nodes.filter((n) => n.id !== id);
  data.edges = data.edges.filter((e) => e.fromId !== id && e.toId !== id);
  save(projectId, data);
}

export function addEdge(
  projectId: string,
  fromId: string,
  toId: string,
  label: string,
  reason: string,
  aiGenerated: boolean
): IdeaEdge {
  const data = load(projectId);
  // Deduplicate
  const exists = data.edges.find(
    (e) =>
      (e.fromId === fromId && e.toId === toId) ||
      (e.fromId === toId && e.toId === fromId)
  );
  if (exists) return exists;
  const edge: IdeaEdge = {
    id: crypto.randomUUID(),
    fromId,
    toId,
    label,
    reason,
    aiGenerated,
  };
  data.edges.push(edge);
  save(projectId, data);
  return edge;
}

export function deleteEdge(projectId: string, id: string): void {
  const data = load(projectId);
  data.edges = data.edges.filter((e) => e.id !== id);
  save(projectId, data);
}

export function replaceAllEdges(projectId: string, edges: IdeaEdge[]): void {
  const data = load(projectId);
  data.edges = edges;
  save(projectId, data);
}

export function saveMindmap(projectId: string, mindmap: MindmapData | null): void {
  const data = load(projectId);
  data.mindmap = mindmap;
  save(projectId, data);
}

export function getMindmap(projectId: string): MindmapData | null {
  return load(projectId).mindmap ?? null;
}

export function saveNodePositions(
  projectId: string,
  positions: { id: string; x: number; y: number }[]
): void {
  const data = load(projectId);
  for (const pos of positions) {
    const idx = data.nodes.findIndex((n) => n.id === pos.id);
    if (idx !== -1) {
      data.nodes[idx].x = pos.x;
      data.nodes[idx].y = pos.y;
    }
  }
  save(projectId, data);
}
